import { useEffect, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useScope, useScopeLabel } from '../hooks/useScope';
import { api } from '../api/client';
import type { ListingSort } from '../api/types';
import { useQuery } from '../hooks/useApi';
import { Banner, EmptyState, ErrorState, Spinner } from '../components/States';
import { ListingRow } from '../components/ListingRow';
import { Lightbox } from '../components/Lightbox';
import { Pagination } from '../components/Pagination';
import { StyleFilter } from '../components/StyleFilter';
import { ListingFilters } from '../components/ListingFilters';
import { FavoriteStar } from '../components/FavoriteStar';
import { useI18n } from '../i18n';
import type { MessageKey } from '../i18n/messages';
import { itemDoAnuncio, reais, trackViewItem } from '../lib/tracking';

// Pattern saiu daqui: virou filtro por número exato, que é como se procura
// pattern. Ordenar por seed não dizia nada a ninguém.
const SORTS: Array<{ value: ListingSort; key: MessageKey }> = [
  { value: 'price_asc', key: 'sort.priceAsc' },
  { value: 'price_desc', key: 'sort.priceDesc' },
  { value: 'float_asc', key: 'sort.floatAsc' },
  { value: 'float_desc', key: 'sort.floatDesc' },
];

const LIMITS = [10, 20, 50, 0];

export function ListingsPage() {
  const scope = useScope();
  const scopeLabel = useScopeLabel(scope);
  const { t } = useI18n();
  const { templateId = '' } = useParams();
  const [params, setParams] = useSearchParams();
  const id = Number(templateId);

  const sort = (params.get('sort') as ListingSort) ?? 'price_asc';
  const limit = Number(params.get('limit') ?? 20);
  const page = Number(params.get('page') ?? 1);
  const floatMin = params.get('floatMin') ?? '';
  const floatMax = params.get('floatMax') ?? '';
  const seed = params.get('seed') ?? '';

  // Os estilos viajam na URL como p1,ruby — o link continua compartilhável.
  const styles = (params.get('styles') ?? '')
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);

  // O anúncio segue junto da foto: é ele que dá acesso às capturas de
  // inspeção e ao link de abrir a peça no CS2.
  const [lightbox, setLightbox] = useState<{
    url: string;
    caption: string;
    commodityId: string | null;
    inspectUrl: string | null;
  } | null>(null);

  const patch = (next: Record<string, string | null>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value === null) merged.delete(key);
      else merged.set(key, value);
    }
    if (!('page' in next)) merged.set('page', '1');
    setParams(merged, { replace: true });
  };

  const listings = useQuery(
    () =>
      api.listings({
        templateId: id,
        sort,
        limit,
        page,
        pageSize: 50,
        type: scope.weaponCode || undefined,
        styles: styles.join(',') || undefined,
        floatMin: floatMin || undefined,
        floatMax: floatMax || undefined,
        seed: seed || undefined,
      }),
    [id, sort, limit, page, scope.weaponCode, styles.join(','), floatMin, floatMax, seed],
    Number.isFinite(id) && id > 0,
  );

  // A página de anúncios é a página do produto: uma skin num desgaste. Conta
  // uma vez por skin, e não a cada filtro, página ou ordenação trocada.
  const produtoVisto = useRef<number | null>(null);
  useEffect(() => {
    const itens = listings.data?.items ?? [];
    if (itens.length === 0 || produtoVisto.current === id) return;
    produtoVisto.current = id;
    const precos = itens.map((l) => l.price).filter((p): p is number => p !== null);
    const item = itemDoAnuncio(itens[0]!);
    // O preço do produto é o da peça mais barata à venda, não o da primeira da lista.
    trackViewItem({ ...item, price: precos.length ? reais(Math.min(...precos)) : undefined });
  }, [listings.data, id]);

  const temFiltroLocal = Boolean(floatMin || floatMax || seed || styles.length);
  const firstIcon = listings.data?.items.find((l) => l.iconUrl)?.iconUrl ?? null;
  const title = listings.data?.items[0]?.name ?? `templateId ${templateId}`;

  return (
    <>
      <div className="crumbs">
        <Link to={scope.basePath}>{scopeLabel}</Link>
        <span className="sep">›</span>
        <span>{t('level3.title')}</span>
        <span className="sep">›</span>
        <b>{title}</b>
        <FavoriteStar templateId={id} name={title} />
      </div>

      {listings.data && (
        <StyleFilter
          disponiveis={listings.data.styles}
          selecionados={styles}
          parcial={listings.data.stylesPartial}
          onChange={(next) => patch({ styles: next.length ? next.join(',') : null })}
        />
      )}

      <ListingFilters
        valores={{ floatMin, floatMax, seed }}
        onApply={(next) =>
          patch({
            floatMin: next.floatMin || null,
            floatMax: next.floatMax || null,
            seed: next.seed || null,
          })
        }
      />

      <div className="toolbar">
        <select className="select" value={sort} onChange={(e) => patch({ sort: e.target.value })}>
          {SORTS.map((s) => (
            <option key={s.value} value={s.value}>
              {t(s.key)}
            </option>
          ))}
        </select>
        <select className="select" value={limit} onChange={(e) => patch({ limit: e.target.value })}>
          {LIMITS.map((l) => (
            <option key={l} value={l}>
              {l === 0 ? t('level3.allOnPage') : t('level3.top', { n: l })}
            </option>
          ))}
        </select>
        <button className="btn ghost" onClick={() => listings.refetch()} disabled={listings.loading}>
          ↻ {t('level3.refresh')}
        </button>
        {/* Com filtro ligado, "m de s" conta a varredura inteira: s são todos
            os anúncios lidos da skin, não os da página aberta. */}
        <span className="count-tag">
          {!listings.data
            ? ''
            : temFiltroLocal
              ? t('level3.filtered', { m: listings.data.matched, s: listings.data.scanned }) +
                ' · ' +
                t('level3.scannedPages', { n: listings.data.pagesScanned })
              : t('level3.shown', { n: listings.data.items.length }) +
                (listings.data.marketTotal ? ' · ' + t('level3.ofTotal', { n: listings.data.marketTotal }) : '')}
        </span>
      </div>

      {listings.data?.source === 'demo' && <Banner kind="warn">{listings.data.notice}</Banner>}
      {listings.data?.scanTruncated && <Banner kind="warn">{t('level3.scanTruncated')}</Banner>}
      {listings.error && <ErrorState error={listings.error} onRetry={listings.refetch} />}
      {listings.loading && <Spinner label={t('level3.loading')} />}

      {!listings.loading && listings.data && listings.data.items.length === 0 && (
        <EmptyState>{t('level3.empty')}</EmptyState>
      )}

      {listings.data?.items.map((listing, index) => (
        <ListingRow
          key={listing.id}
          listing={listing}
          rank={index + 1}
          fallbackIcon={firstIcon}
          onOpenImage={(url, caption, commodityId, inspectUrl) =>
            setLightbox({ url, caption, commodityId, inspectUrl })
          }
        />
      ))}

      {listings.data && (
        <Pagination
          page={listings.data.pageIndex}
          pageSize={listings.data.pageSize}
          total={listings.data.totalCount}
          hasNext={listings.data.hasNext}
          busy={listings.loading}
          onChange={(next) => patch({ page: String(next) })}
        />
      )}

      <Lightbox
        url={lightbox?.url ?? null}
        caption={lightbox?.caption}
        commodityId={lightbox?.commodityId}
        inspectUrl={lightbox?.inspectUrl}
        onClose={() => setLightbox(null)}
      />
    </>
  );
}
